/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/no-multi-comp */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TableCell,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useDropzone } from 'react-dropzone';
import Viewer from 'react-viewer';
import TableWidgetV2 from '../TableWidgetV2';
import DialogDefaultV2 from '../DialogDefaultV2';
import { v4 as uuidv4 } from 'uuid';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import fileDownload from 'js-file-download';
import heic2any from 'heic2any';
import { Document, Page, pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const DropzoneDialogV2 = ({
  open,
  onClose,
  onSave,
  filesLimit,
  maxFileSize,
  dialogTitle,
  cancelButtonText,
  submitButtonText,
  dropzoneText,
  previewText,
  maxWidth,
  getFileLimitExceedMessage,
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [rejectMessage, setRejectMessage] = useState('');

  useEffect(() => {
    if (open) {
      setSelectedFiles([]);
      setRejectMessage('');
    }
  }, [open]);

  const onDrop = useCallback(
    (acceptedFiles, fileRejections) => {
      if (fileRejections.length) {
        const tooLarge = fileRejections.some((rej) =>
          rej.errors.some((e) => e.code === 'file-too-large'),
        );
        setRejectMessage(
          tooLarge
            ? `Um ou mais arquivos excedem o tamanho máximo permitido (${Math.round(maxFileSize / 1000000)} MB).`
            : 'Não foi possível aceitar um ou mais arquivos.',
        );
      } else {
        setRejectMessage('');
      }

      setSelectedFiles((prev) => {
        const remaining = filesLimit - prev.length;
        if (remaining <= 0) {
          if (acceptedFiles.length > 0) {
            window.alert(getFileLimitExceedMessage(filesLimit));
          }
          return prev;
        }
        const toAdd = acceptedFiles.slice(0, remaining);
        if (acceptedFiles.length > remaining) {
          window.alert(getFileLimitExceedMessage(filesLimit));
        }
        return [...prev, ...toAdd];
      });
    },
    [filesLimit, getFileLimitExceedMessage, maxFileSize],
  );

  const atLimit = selectedFiles.length >= filesLimit;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: maxFileSize,
    multiple: true,
    disabled: atLimit,
  });

  const removeAt = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setRejectMessage('');
  };

  const handleCancel = () => {
    onClose();
  };

  const handleSubmit = () => {
    onSave(selectedFiles);
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
        handleCancel();
      }}
      maxWidth={maxWidth}
      fullWidth
    >
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Box
          {...getRootProps()}
          sx={(theme) => ({
            border: '2px dashed',
            borderColor: isDragActive
              ? theme.palette.primary.main
              : theme.palette.divider,
            borderRadius: 1,
            p: 3,
            mt: 1,
            textAlign: 'center',
            cursor: atLimit ? 'not-allowed' : 'pointer',
            bgcolor: isDragActive
              ? theme.palette.action.hover
              : theme.palette.background.paper,
            transition: theme.transitions.create([
              'border-color',
              'background-color',
            ]),
            ...(atLimit
              ? {}
              : {
                '&:hover': {
                  bgcolor: theme.palette.action.hover,
                  borderColor: theme.palette.primary.main,
                },
              }),
          })}
        >
          <input {...getInputProps()} />
          <Typography variant="body2" color="text.secondary">
            {dropzoneText}
          </Typography>
        </Box>
        {rejectMessage ? (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
            {rejectMessage}
          </Typography>
        ) : null}
        {selectedFiles.length > 0 ? (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              {previewText}
            </Typography>
            <List dense disablePadding>
              {selectedFiles.map((file, index) => {
                const isImage = file.type.startsWith('image/');
                const previewUrl = isImage ? URL.createObjectURL(file) : null;
                return (
                  <ListItem
                    key={`${file.name}-${file.lastModified}-${index}`}
                    secondaryAction={
                      <IconButton edge="end" aria-label="remove" onClick={() => removeAt(index)} size="small">
                        <DeleteOutlineIcon />
                      </IconButton>
                    }
                    sx={{ alignItems: 'flex-start', py: 1 }}
                  >
                    {previewUrl ? (
                      <Box
                        component="img"
                        alt=""
                        src={previewUrl}
                        sx={{
                          width: 48,
                          height: 48,
                          objectFit: 'cover',
                          borderRadius: 1,
                          mr: 2,
                          flexShrink: 0,
                        }}
                        onLoad={() => URL.revokeObjectURL(previewUrl)}
                      />
                    ) : null}
                    <ListItemText
                      primary={file.name}
                      secondary={`${(file.size / 1024).toFixed(1)} KB`}
                    />
                  </ListItem>
                );
              })}
            </List>
          </>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCancel}>{cancelButtonText}</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={selectedFiles.length === 0}>
          {submitButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DropzoneDialogV2.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  filesLimit: PropTypes.number.isRequired,
  maxFileSize: PropTypes.number.isRequired,
  dialogTitle: PropTypes.string.isRequired,
  cancelButtonText: PropTypes.string.isRequired,
  submitButtonText: PropTypes.string.isRequired,
  dropzoneText: PropTypes.string.isRequired,
  previewText: PropTypes.string.isRequired,
  maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']).isRequired,
  getFileLimitExceedMessage: PropTypes.func.isRequired,
};

const ArquivoContainerV2 = ({
  arquivos,
  onSave,
  disabled,
  showDescription = false,
  headerTitle = 'Arquivos',
  addTitle = 'Novo',
  hasAdd = true,
  showDelete = true,
  filesLimit = 10,
  shouldSaveSync = false,
  collapse = false,
  initialExpanded = true,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [files, setFiles] = useState([]);
  const [noPdfsFiles, setNoPdfsFiles] = useState([]);
  const [openPdfView, setOpenPdfView] = useState(false);
  const [currentPdf, setCurrentPdf] = useState(null);
  const [numPages, setNumPages] = useState(1);
  const [open, setOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const getBase64 = (image) =>
    image &&
    (image.includes('data:image') || image.includes('data:application'))
      ? image
      : `data:image/png;base64,${image}`;

  const toBase64 = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve('');
    });

  useEffect(() => {
    if (arquivos) {
      const getArquivos = async () => {
        const nextFiles = [];
        const noPdfs = [];
        for (const arquivo of arquivos) {
          if (arquivo.file && arquivo.file.size && arquivo.extensao === 'pdf') {
            nextFiles.push(arquivo);
          } else {
            if (arquivo.file && arquivo.file.size && arquivo.extensao === 'heic') {
              let file = arquivo.file;
              try {
                file = await heic2any({
                  blob: arquivo.file,
                  toType: 'image/jpeg',
                  quality: 0.5,
                });
              } catch (e) {
                console.error(e);
              }
              arquivo.file = file;
              arquivo.src = await toBase64(file);
            } else if (arquivo.file && arquivo.file.size && !arquivo.src) {
              arquivo.src = await toBase64(arquivo.file);
            }
            noPdfs.push(arquivo);
            nextFiles.push(arquivo);
          }
        }
        setNoPdfsFiles(noPdfs);
        setFiles(nextFiles);
      };

      getArquivos();
    }
  }, [arquivos]);

  const onSaveLocal = async (filesToSave) => {
    const arquivoList = [];

    for (const file of filesToSave) {
      arquivoList.push({
        id: uuidv4(),
        nome: file.name.split('.')[0],
        extensao: file.name.split('.').pop(),
        alt: file.name,
        src: getBase64(await toBase64(file)),
        file,
        new: true,
      });
    }

    if (shouldSaveSync) {
      onSave(arquivoList);
    } else {
      setFiles([...files, ...arquivoList]);
      onSave([...files, ...arquivoList]);
    }
    setOpen(false);
  };

  const renderRows = (file) => {
    if (showDescription) {
      return (
        <>
          <TableCell>{file.nome}</TableCell>
          <TableCell>{file.extensao}</TableCell>
          <TableCell>{file.description}</TableCell>
        </>
      );
    }
    return (
      <>
        <TableCell>{file.nome}</TableCell>
        <TableCell>{file.extensao}</TableCell>
      </>
    );
  };

  const onDeleteLocal = (id) => {
    const next = files.filter((f) => f.id !== id);
    setFiles(next);
    onSave(next);
  };

  return (
    <>
      <Grid size={{ xs: 12 }} sx={{ pb: 0 }}>
        <TableWidgetV2
          rows={files}
          addTitle={addTitle}
          titles={
            showDescription
              ? ['Nome', 'Extensão', 'Descrição']
              : ['Nome', 'Extensão']
          }
          headerTitle={headerTitle}
          deleteMessage="O arquivo será removido e não poderá ser mais recuperado."
          renderRows={renderRows}
          hasRowActions
          showEmptyState={false}
          showEdit={false}
          onDelete={onDeleteLocal}
          hasAdd={hasAdd && (disabled === undefined || disabled === false)}
          showDelete={showDelete && (disabled === undefined || disabled === false)}
          onAdd={() => setOpen(true)}
          collapse={collapse}
          initialExpanded={initialExpanded}
          actions={[
            {
              key: 'view',
              onClick: (id) => {
                const currentFile = files.filter((f) => f.id === id)[0];
                if (currentFile.extensao === 'pdf') {
                  setCurrentPdf(currentFile);
                  setOpenPdfView(true);
                } else {
                  setActiveIndex(noPdfsFiles.map((f) => f.id).indexOf(id));
                  setShowModal(true);
                }
              },
              icon: <PhotoCameraIcon fontSize="small" />,
            },
            {
              key: 'download',
              onClick: (id) => {
                const file = files.filter((f) => f.id === id)[0];
                fileDownload(file.file, `${file.nome}.${file.extensao}`);
              },
              icon: <CloudDownloadIcon fontSize="small" />,
            },
          ]}
        />
      </Grid>
      <DropzoneDialogV2
        open={open}
        onSave={onSaveLocal}
        maxFileSize={20000000}
        onClose={() => setOpen(false)}
        filesLimit={filesLimit}
        previewText="Pré-visualização: "
        dialogTitle="Selecionar arquivos"
        cancelButtonText="Cancelar"
        submitButtonText="Salvar"
        dropzoneText="Arraste e solte um arquivo aqui ou clique"
        maxWidth="md"
        getFileLimitExceedMessage={(limit) =>
          `Número máximo permitido de arquivos excedido. Apenas ${limit} permitidos`
        }
      />
      <Viewer
        visible={showModal}
        onClose={() => {
          setActiveIndex(0);
          setShowModal(false);
        }}
        images={noPdfsFiles || []}
        zIndex={3000}
        scalable={false}
        noImgDetails
        zoomSpeed={0.25}
        defaultImg={{
          src: '/images/noImage.jpeg',
        }}
        activeIndex={activeIndex}
      />
      <DialogDefaultV2
        open={openPdfView}
        handlerCancel={() => setOpenPdfView(false)}
        readOnly
        title={currentPdf ? currentPdf.nome : ''}
        maxWidth="md"
      >
        <Document
          file={currentPdf ? currentPdf.file : null}
          onLoadSuccess={({ numPages: n }) => setNumPages(n)}
        >
          <Page pageNumber={numPages} />
        </Document>
      </DialogDefaultV2>
    </>
  );
};

ArquivoContainerV2.propTypes = {
  arquivos: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  showDescription: PropTypes.bool,
  headerTitle: PropTypes.string,
  addTitle: PropTypes.string,
  hasAdd: PropTypes.bool,
  showDelete: PropTypes.bool,
  filesLimit: PropTypes.number,
  shouldSaveSync: PropTypes.bool,
  collapse: PropTypes.bool,
  initialExpanded: PropTypes.bool,
};

ArquivoContainerV2.defaultProps = {
  disabled: false,
  showDescription: false,
};

export default ArquivoContainerV2;
